import { useState } from "react";
import { z } from "zod";
import emailjs from "@emailjs/browser";

const contactSchema = z.object({
  name: z
    .string()
    .trim()
    .min(1, { message: "Name is required" })
    .max(100, { message: "Name must be less than 100 characters" })
    .regex(/^[a-zA-Z\s'.-]+$/, { message: "Name contains invalid characters" }),
  phone: z
    .string()
    .trim()
    .min(7, { message: "Please enter a valid phone number" })
    .max(20, { message: "Phone number is too long" })
    .regex(/^[0-9+()\-.\s]+$/, { message: "Phone number contains invalid characters" }),
  email: z
    .string()
    .trim()
    .max(255, { message: "Email must be less than 255 characters" })
    .email({ message: "Invalid email address" })
    .or(z.literal("")),
  serviceType: z
    .string()
    .max(50)
    .optional(),
  message: z
    .string()
    .trim()
    .min(1, { message: "Project details are required" })
    .max(2000, { message: "Message must be less than 2000 characters" }),
});
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { Phone, MessageSquare, Mail, MapPin, Send, ExternalLink, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";

// EmailJS Configuration - Replace these with your actual IDs from emailjs.com
const EMAILJS_SERVICE_ID = "YOUR_SERVICE_ID";
const EMAILJS_TEMPLATE_ID = "YOUR_TEMPLATE_ID";
const EMAILJS_PUBLIC_KEY = "YOUR_PUBLIC_KEY";

const Contact = () => {
  const { toast } = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    phone: "",
    email: "",
    serviceType: "",
    message: "",
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const result = contactSchema.safeParse(formData);
    if (!result.success) {
      const firstError = result.error.errors[0]?.message ?? "Please check your inputs";
      toast({
        title: "Invalid input",
        description: firstError,
        variant: "destructive",
      });
      return;
    }

    setIsSubmitting(true);
    const validated = result.data;

    try {
      await emailjs.send(
        EMAILJS_SERVICE_ID,
        EMAILJS_TEMPLATE_ID,
        {
          from_name: validated.name,
          from_phone: validated.phone,
          from_email: validated.email || "Not provided",
          service_type: validated.serviceType || "Not specified",
          message: validated.message,
        },
        EMAILJS_PUBLIC_KEY
      );

      toast({
        title: "Message Sent!",
        description: "We'll get back to you as soon as possible.",
      });
      setFormData({ name: "", phone: "", email: "", serviceType: "", message: "" });
    } catch (error) {
      console.error("EmailJS error:", error);
      toast({
        title: "Error Sending Message",
        description: "Please try calling or texting us instead.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleServiceTypeChange = (value: string) => {
    setFormData({ ...formData, serviceType: value });
  };

  return (
    <div className="min-h-screen">
      <Header />
      <main className="pt-20">
        {/* Hero Section */}
        <section className="bg-primary py-16">
          <div className="container mx-auto px-4 text-center">
            <h1 className="text-4xl md:text-5xl font-heading font-bold text-primary-foreground mb-4">
              Contact Us
            </h1>
            <p className="text-primary-foreground/80 text-lg max-w-2xl mx-auto">
              Ready to start your flooring project? Get in touch for a free estimate.
            </p>
          </div>
        </section>

        {/* Contact Options */}
        <section className="py-16 bg-background">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              {/* Priority Contact Methods */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-12">
                {/* Text First - Fastest */}
                <a
                  href="sms:+17192327424"
                  className="bg-accent text-accent-foreground p-8 rounded-lg text-center hover:bg-accent/90 transition-colors group"
                >
                  <MessageSquare className="w-12 h-12 mx-auto mb-4 group-hover:scale-110 transition-transform" />
                  <h2 className="font-heading font-bold text-2xl mb-2">Text Us</h2>
                  <p className="text-accent-foreground/80 mb-4">Fastest Response</p>
                  <span className="font-heading text-xl">(719) 232-7424</span>
                </a>

                {/* Call Second */}
                <a
                  href="tel:+17192327424"
                  className="bg-primary text-primary-foreground p-8 rounded-lg text-center hover:bg-primary/90 transition-colors group"
                >
                  <Phone className="w-12 h-12 mx-auto mb-4 group-hover:scale-110 transition-transform" />
                  <h2 className="font-heading font-bold text-2xl mb-2">Call Now</h2>
                  <p className="text-primary-foreground/80 mb-4">Talk to Us Directly</p>
                  <span className="font-heading text-xl">(719) 232-7424</span>
                </a>
              </div>

              {/* Location */}
              <div className="flex items-center justify-center gap-2 text-muted-foreground mb-8">
                <MapPin className="w-5 h-5" />
                <span>Serving Colorado Springs & Surrounding Areas</span>
              </div>

              {/* Google Maps Embed */}
              <div className="mb-12 rounded-lg overflow-hidden shadow-md">
                <iframe
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d206158.74313697012!2d-104.9178369!3d38.8339578!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x8713412ea1e6d22b%3A0x418eeb92f5e86b13!2sColorado%20Springs%2C%20CO!5e0!3m2!1sen!2sus!4v1706000000000!5m2!1sen!2sus"
                  width="100%"
                  height="300"
                  style={{ border: 0 }}
                  allowFullScreen
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="Service Area - Colorado Springs"
                  className="w-full"
                />
              </div>

              {/* Contact Form - Last */}
              <div className="bg-card rounded-lg p-8 shadow-md">
                <h2 className="font-heading font-bold text-2xl mb-6 text-center">Send Us a Message</h2>
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label htmlFor="name" className="block font-medium text-sm mb-2">
                        Your Name *
                      </label>
                      <Input
                        id="name"
                        name="name"
                        type="text"
                        required
                        maxLength={100}
                        value={formData.name}
                        onChange={handleChange}
                        placeholder="John Smith"
                        className="w-full"
                      />
                    </div>
                    <div>
                      <label htmlFor="phone" className="block font-medium text-sm mb-2">
                        Phone Number *
                      </label>
                      <Input
                        id="phone"
                        name="phone"
                        type="tel"
                        required
                        maxLength={20}
                        value={formData.phone}
                        onChange={handleChange}
                        placeholder="(719) 555-1234"
                        className="w-full"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label htmlFor="email" className="block font-medium text-sm mb-2">
                        Email Address
                      </label>
                      <Input
                        id="email"
                        name="email"
                        type="email"
                        maxLength={255}
                        value={formData.email}
                        onChange={handleChange}
                        placeholder="john@example.com"
                        className="w-full"
                      />
                    </div>
                    <div>
                      <label htmlFor="serviceType" className="block font-medium text-sm mb-2">
                        Service Type (optional)
                      </label>
                      <Select value={formData.serviceType} onValueChange={handleServiceTypeChange}>
                        <SelectTrigger className="w-full">
                          <SelectValue placeholder="Select a service..." />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="hardwood">Hardwood Installation</SelectItem>
                          <SelectItem value="sand-finish">Sand & Finish</SelectItem>
                          <SelectItem value="lvp-laminate">LVP / Laminate</SelectItem>
                          <SelectItem value="stairs">Stairs</SelectItem>
                          <SelectItem value="not-sure">Not sure yet</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div>
                    <label htmlFor="message" className="block font-medium text-sm mb-2">
                      Project Details *
                    </label>
                    <Textarea
                      id="message"
                      name="message"
                      required
                      maxLength={2000}
                      value={formData.message}
                      onChange={handleChange}
                      placeholder="Tell us about your flooring project..."
                      rows={5}
                      className="w-full"
                    />
                  </div>
                  <Button type="submit" className="w-full btn-cta-primary" disabled={isSubmitting}>
                    {isSubmitting ? (
                      <>
                        <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                        Sending...
                      </>
                    ) : (
                      <>
                        <Send className="w-5 h-5 mr-2" />
                        Send Message
                      </>
                    )}
                  </Button>
                </form>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default Contact;
